import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Input, Badge } from '@tkpod/ui';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';

interface Preset {
  id: string;
  blankVariantId: string | null;
  name: string;
  sku: string | null;
  maxWidth: number | null;
  maxHeight: number | null;
  sortOrder: number;
  isActive: boolean;
}

interface EditForm {
  maxWidth: string;
  maxHeight: string;
  name: string;
}

export function CatalogPresetsTab({
  templateId,
  presets: initialPresets,
  designerMode,
  onDirtyChange,
}: {
  templateId: string;
  presets: unknown[];
  designerMode: string;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const allPresets = (initialPresets ?? []) as Preset[];
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<EditForm>({ maxWidth: '', maxHeight: '', name: '' });

  const activePresets = allPresets.filter((p) => p.blankVariantId && p.isActive);
  const legacyPresets = allPresets.filter((p) => !p.blankVariantId && p.isActive);
  const hasLegacyOnly = activePresets.length === 0 && legacyPresets.length > 0;
  const unconfiguredCount = activePresets.filter(
    (p) => p.maxWidth == null || p.maxHeight == null,
  ).length;

  const { data: variantCount } = useQuery<number>({
    queryKey: ['catalog-variant-count', templateId],
    queryFn: async () => {
      const res = await api.get<{ pagination: { total: number } }>(
        `/api/v1/admin/catalog/${templateId}/variants?isActive=true&limit=1`,
      );
      return res.pagination?.total ?? 0;
    },
    enabled: designerMode !== 'NONE',
  });

  const isOutOfSync = variantCount != null && variantCount !== activePresets.length;

  useEffect(() => {
    onDirtyChange?.(editId !== null);
  }, [editId, onDirtyChange]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['catalog-template', templateId] });

  const syncMutation = useMutation({
    mutationFn: () => api.post(`/api/v1/admin/catalog/templates/${templateId}/sync-presets`, {}),
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['catalog-variant-count', templateId] });
      toast('Designer presets synced from variants.');
    },
    onError: (err: Error) => {
      toast(err.message || 'Failed to sync presets.', 'error');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.patch(`/api/v1/admin/catalog/presets/${id}`, body),
    onSuccess: () => {
      invalidate();
      setEditId(null);
      toast('Designer preset updated.');
    },
    onError: (err: Error) => {
      toast(err.message || 'Failed to update preset.', 'error');
    },
  });

  const openEdit = (p: Preset) => {
    setEditId(p.id);
    setForm({
      maxWidth: p.maxWidth != null ? String(p.maxWidth) : '',
      maxHeight: p.maxHeight != null ? String(p.maxHeight) : '',
      name: p.name,
    });
  };

  const handleSave = () => {
    if (!editId) return;
    const body: Record<string, unknown> = {};
    if (form.name) body.name = form.name;
    body.maxWidth = form.maxWidth ? parseFloat(form.maxWidth) : null;
    body.maxHeight = form.maxHeight ? parseFloat(form.maxHeight) : null;
    updateMutation.mutate({ id: editId, body });
  };

  if (designerMode === 'NONE') {
    return (
      <Card>
        <div className="px-6 py-12 text-center">
          <p className="text-sm text-text-secondary">
            Designer mode is set to <strong>None</strong>. Designer presets are not available for this template.
          </p>
          <p className="mt-1 text-xs text-text-tertiary">
            Change the designer mode in the Overview tab to enable designer presets.
          </p>
        </div>
      </Card>
    );
  }

  const renderPresetRow = (p: Preset) => {
    const isEditing = editId === p.id;

    if (isEditing) {
      return (
        <tr key={p.id} className="border-b border-border bg-brand-50/20">
          <td className="px-3 py-2">
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="text-sm"
            />
          </td>
          <td className="px-3 py-2">
            <Input
              type="number"
              step="0.1"
              placeholder="Width (in)"
              value={form.maxWidth}
              onChange={(e) => setForm({ ...form, maxWidth: e.target.value })}
              className="w-24 text-sm"
            />
          </td>
          <td className="px-3 py-2">
            <Input
              type="number"
              step="0.1"
              placeholder="Height (in)"
              value={form.maxHeight}
              onChange={(e) => setForm({ ...form, maxHeight: e.target.value })}
              className="w-24 text-sm"
            />
          </td>
          <td className="px-3 py-2">
            <div className="flex gap-1">
              <Button
                size="sm"
                onClick={handleSave}
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditId(null)}>
                Cancel
              </Button>
            </div>
          </td>
        </tr>
      );
    }

    return (
      <tr key={p.id} className="border-b border-border last:border-0">
        <td className="px-3 py-2 font-medium">
          {p.name}
          {p.sku ? <span className="ml-1 text-xs text-text-tertiary">({p.sku})</span> : ''}
        </td>
        <td className="px-3 py-2">
          {p.maxWidth != null ? (
            <span>{p.maxWidth}"</span>
          ) : (
            <Badge variant="warning" size="sm">--</Badge>
          )}
        </td>
        <td className="px-3 py-2">
          {p.maxHeight != null ? (
            <span>{p.maxHeight}"</span>
          ) : (
            <Badge variant="warning" size="sm">--</Badge>
          )}
        </td>
        <td className="px-3 py-2">
          <Button size="sm" variant="ghost" onClick={() => openEdit(p)}>
            Edit
          </Button>
        </td>
      </tr>
    );
  };

  return (
    <Card>
      <div className="px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-text-primary">
              Designer Presets ({activePresets.length})
            </h3>
            <p className="mt-0.5 text-xs text-text-tertiary">
              Presets are auto-synced from variants. Configure max canvas dimensions for the designer.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
          >
            {syncMutation.isPending ? 'Syncing...' : 'Sync from Variants'}
          </Button>
        </div>

        {isOutOfSync && (
          <div className="mb-4 rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700 flex items-center justify-between">
            <div>
              <strong>Presets out of sync.</strong>{' '}
              {variantCount} active variant{variantCount !== 1 ? 's' : ''} but{' '}
              {activePresets.length} preset{activePresets.length !== 1 ? 's' : ''}.
              Sync to update presets from current variants.
            </div>
            <Button
              size="sm"
              variant="danger"
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
            >
              {syncMutation.isPending ? 'Syncing...' : 'Sync Now'}
            </Button>
          </div>
        )}

        {hasLegacyOnly && (
          <div className="mb-4 rounded-md border border-warning-200 bg-warning-50 p-3 text-sm text-warning-700 flex items-center justify-between">
            <div>
              <strong>Legacy presets detected.</strong>{' '}
              {legacyPresets.length} preset{legacyPresets.length !== 1 ? 's are' : ' is'} not linked to any variant.
              Sync from variants to replace them with variant-linked presets.
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
            >
              {syncMutation.isPending ? 'Syncing...' : 'Sync Now'}
            </Button>
          </div>
        )}

        {unconfiguredCount > 0 && (
          <div className="mb-4 rounded-md border border-warning-200 bg-warning-50 p-3 text-sm text-warning-700">
            {unconfiguredCount} preset{unconfiguredCount > 1 ? 's' : ''} missing max
            width/height configuration. The designer may not enforce canvas limits correctly.
          </div>
        )}

        {activePresets.length === 0 && !hasLegacyOnly ? (
          <div className="py-12 text-center">
            <p className="text-sm font-medium text-text-secondary">No designer presets</p>
            <p className="mt-1 text-xs text-text-tertiary">
              Generate variants first, then sync presets to create them automatically.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-text-secondary">
                  <th className="px-3 py-2 font-medium">Name</th>
                  <th className="px-3 py-2 font-medium">Max Width</th>
                  <th className="px-3 py-2 font-medium">Max Height</th>
                  <th className="px-3 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {activePresets.map(renderPresetRow)}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}
